import { useState, useEffect, useCallback, useRef } from 'react';
import { notificationWS } from '../services/pushNotificationService';

/**
 * Hook for managing real-time inventory updates
 * 
 * Tracks inventory changes for displayed items and provides
 * real-time updates via WebSocket.
 * 
 * @param {string} token - JWT token for WebSocket authentication
 * @returns {object} - Inventory state and helpers
 */
export function useInventoryUpdates(token) {
  // Map of rshd_id -> { quantity, status, lastUpdate }
  const [inventoryState, setInventoryState] = useState({});
  const [isConnected, setIsConnected] = useState(false);
  const listenersRef = useRef([]);

  // Connect to WebSocket when token is available
  useEffect(() => {
    if (!token) return;

    notificationWS.connect(token);

    // Connection status
    const unsubConnect = notificationWS.on('connected', () => {
      setIsConnected(true);
    });

    const unsubDisconnect = notificationWS.on('disconnected', () => {
      setIsConnected(false);
    });

    // Inventory update handler
    const unsubInventory = notificationWS.on('inventory_update', (data) => {
      if (data?.data?.rshd_id) {
        setInventoryState(prev => ({
          ...prev,
          [data.data.rshd_id]: {
            quantity: data.data.quantity,
            status: data.data.status || 'available',
            lastUpdate: data.timestamp || new Date().toISOString(),
            itemName: data.data.item_name
          }
        }));
      }
    });

    // Low stock handler
    const unsubLowStock = notificationWS.on('low_stock', (data) => {
      if (data?.data?.rshd_id) {
        setInventoryState(prev => ({
          ...prev,
          [data.data.rshd_id]: {
            quantity: data.data.quantity,
            status: 'low_stock',
            lastUpdate: data.timestamp || new Date().toISOString(),
            itemName: data.data.item_name
          }
        }));
      }
    });

    // Sold out handler
    const unsubSoldOut = notificationWS.on('sold_out', (data) => {
      if (data?.data?.rshd_id) {
        setInventoryState(prev => ({
          ...prev,
          [data.data.rshd_id]: {
            quantity: 0,
            status: 'sold_out',
            lastUpdate: data.timestamp || new Date().toISOString(),
            itemName: data.data.item_name
          }
        }));
      }
    });

    listenersRef.current = [unsubConnect, unsubDisconnect, unsubInventory, unsubLowStock, unsubSoldOut];

    return () => {
      listenersRef.current.forEach(unsub => unsub?.());
      listenersRef.current = [];
    };
  }, [token]);

  /**
   * Get inventory status for a specific item
   * @param {string} rshdId - The RSHD item ID
   * @param {number} initialQuantity - Fallback quantity if no update received
   * @returns {object} - { quantity, status, isLowStock, isSoldOut }
   */
  const getItemStatus = useCallback((rshdId, initialQuantity = 0) => {
    const update = inventoryState[rshdId];
    
    if (update) {
      return {
        quantity: update.quantity,
        status: update.status,
        isLowStock: update.status === 'low_stock' || (update.quantity > 0 && update.quantity <= 5),
        isSoldOut: update.status === 'sold_out' || update.quantity <= 0,
        hasUpdate: true,
        lastUpdate: update.lastUpdate
      };
    }

    return {
      quantity: initialQuantity,
      status: initialQuantity <= 0 ? 'sold_out' : (initialQuantity <= 5 ? 'low_stock' : 'available'),
      isLowStock: initialQuantity > 0 && initialQuantity <= 5,
      isSoldOut: initialQuantity <= 0,
      hasUpdate: false,
      lastUpdate: null
    };
  }, [inventoryState]);

  /**
   * Check if an item has had a recent update (within last 5 minutes)
   * @param {string} rshdId - The RSHD item ID
   * @returns {boolean}
   */
  const hasRecentUpdate = useCallback((rshdId) => {
    const update = inventoryState[rshdId];
    if (!update?.lastUpdate) return false;

    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    return new Date(update.lastUpdate) > fiveMinutesAgo;
  }, [inventoryState]);

  /**
   * Clear all inventory state (useful on page navigation)
   */
  const clearInventoryState = useCallback(() => {
    setInventoryState({});
  }, []);

  return {
    inventoryState,
    isConnected,
    getItemStatus,
    hasRecentUpdate,
    clearInventoryState
  };
}

export default useInventoryUpdates;
